import apiService, { RestError } from './api-service'
import submissionStore, { LaunchData, QaLaunchData, SellerRatingLaunchData, Source, SubmissionPage }
  from 'store/submission-store'
import { hostsConfig, turnToConfig } from 'init/config'
import submissionLoader from 'widgets/submission/submission-loader'
import standalonePopoverLoader from 'widgets/standalone-popover/standalone-popover-loader'
import msg from './msg'
import { DoMoreProps, MessageType } from 'components/do-more/do-more'
import globalConfig from 'init/global.config'

type LaunchType = 'qa' | 'rev' | 'sr'

export interface SsoContext {
  rating?: number
  sku?: string
  submissionToken?: string
  action?: string
  authSetting?: string
  source?: Source
  page?: SubmissionPage // used when launch from solicitation email
  questionId?: number
  // used when launch from solicitation email
  launchData?: LaunchData | QaLaunchData | SellerRatingLaunchData
  launchType?: LaunchType
}

function encodeSsoContext(ssoContext: SsoContext): string {
  return btoa(JSON.stringify(ssoContext))
}

export function processSsoRegDone(params: { context: string, userDataToken: string }) {
  const ssoContextData = parseSsoContext(params.context)
  if (params.userDataToken === null || params.userDataToken === '') {
    if (ssoContextData.action === 'QUESTION_CREATE' && globalConfig.isAllowAnon()) {
      standalonePopoverLoader.load({ message: msg('ia.reg.skipped'), autoClose: true })
    }
    return
  }
  apiService.completeSsoAction({
    sku: ssoContextData.sku
  }, {
    submissionToken: ssoContextData.submissionToken,
    action: ssoContextData.action,
    userDataToken: params.userDataToken
  }).then((ssoResponse) => {
    // Review submission - purchase required case
    if (ssoContextData.action === 'REVIEW_CREATE' && globalConfig.isPurchaseReq()) {
      const launchData = {
        startAction: 'review',
        fromSso: true,
        source: ssoContextData.source,
        ssoToken: ssoResponse.token,
        starSelected: typeof ssoContextData.rating === 'number' ? ssoContextData.rating : undefined
      } as LaunchData
      submissionStore.startRevFlow(
        ssoContextData.sku,
        hostsConfig.siteKey,
        { page: 'review-form', props: {} } as SubmissionPage,
        launchData
      )
      submissionLoader.load()
    } else {
      let messageType = 'review' as MessageType
      switch (ssoContextData.action) {
        case 'QUESTION_CREATE':
          messageType = globalConfig.isAllowAnon() ? 'questionAnonSso' : 'question'; break
        case 'ANSWER_CREATE': messageType = 'answer'; break
        case 'REPLY_CREATE': messageType = 'reply'
      }
      submissionStore.launchData = { ssoToken: ssoResponse.token }
      submissionStore.currentPage = {
        page: 'do-more',
        props: {
          msgType: messageType,
          questionId: ssoContextData.questionId || null
        } as DoMoreProps
      } as SubmissionPage
      submissionLoader.load()
    }
    // stops spinner on write review button(s)
    submissionStore.isLaunchingWriteReview = false
  }).catch((e: RestError) => {
    const errorMsgKey = e.errorCode === 'item-already-reviewed'
      ? 'revForm.alreadyReviewed.message'
      : e.errorCode === 'turnto-offline' ? 'offline.error' : 'unexpected.error'
    standalonePopoverLoader.load({
      message: msg(errorMsgKey), type: 'error', autoClose: true
    })
  })
}

export function ssoCreateReview(sku, source: Source, submissionToken = null, rating = null) {
  switch (globalConfig.authSettings.review) {
    case 1:
      ssoUserData(sku, 'REVIEW_CREATE', 'REGISTRATION_REQUIRED', submissionToken, source, rating)
      break
    case 3:
      ssoUserData(sku, 'REVIEW_CREATE', 'CONFIRMATION_REQUIRED', submissionToken, source, rating)
      break
    case 4:
      ssoUserData(sku, 'REVIEW_CREATE', 'PURCHASE_REQUIRED', submissionToken, source, rating)
  }
}

export function ssoCreateQuestion(sku, submissionToken = null) {
  switch (globalConfig.authSettings.question) {
    case 1:
      ssoUserData(sku, 'QUESTION_CREATE', 'ANONYMOUS', submissionToken)
      break
    case 2:
      ssoUserData(sku, 'QUESTION_CREATE', 'REGISTRATION_REQUIRED', submissionToken)
      break
    case 3:
      ssoUserData(sku, 'QUESTION_CREATE', 'CONFIRMATION_REQUIRED', submissionToken)
  }
}

export function ssoCreateAnswer(sku, submissionToken, questionId = null) {
  switch (globalConfig.authSettings.answerReply) {
    case 1:
      ssoUserData(
        sku, 'ANSWER_CREATE', 'REGISTRATION_REQUIRED', submissionToken, null, null, questionId
      )
      break
    case 2:
      ssoUserData(
        sku, 'ANSWER_CREATE', 'CONFIRMATION_REQUIRED', submissionToken, null, null, questionId
      )
  }
}

export function ssoCreateReply(sku, submissionToken) {
  switch (globalConfig.authSettings.answerReply) {
    case 1:
      ssoUserData(sku, 'REPLY_CREATE', 'REGISTRATION_REQUIRED', submissionToken)
      break
    case 2:
      ssoUserData(sku, 'REPLY_CREATE', 'CONFIRMATION_REQUIRED', submissionToken)
  }
}

export function ssoGetLoggedInData(
  sku: string,
  page: SubmissionPage,
  launchData: LaunchData | QaLaunchData | SellerRatingLaunchData,
  launchType: LaunchType
) {
  const ssoContext = {} as SsoContext
  ssoContext.sku = sku
  ssoContext.page = page
  ssoContext.launchData = launchData
  ssoContext.launchType = launchType
  const encodedSsoContext = encodeSsoContext(ssoContext) as string
  turnToConfig.sso.loggedInDataFn(encodedSsoContext)
}

export function ssoGetLoggedInDataForReview(sku, source, rating = null) {
  ssoGetLoggedInData(
    sku,
    { page: 'review-form', props: {} } as SubmissionPage,
    {
      startAction: 'review',
      source: typeof source === 'number' ? source : undefined,
      starSelected: typeof rating === 'number' ? rating : undefined
    },
    'rev'
  )
}

export function processLoggedInDataDone(params: { context: string, loggedInData: string }) {
  const ssoContextData = parseSsoContext(params.context)
  if (!!params.loggedInData) {
    ssoContextData.launchData.ssoProfileToken = params.loggedInData
  }
  if (ssoContextData.launchType === 'qa') {
    submissionStore.startQaFlow(
      ssoContextData.sku,
      hostsConfig.siteKey,
      ssoContextData.page,
      ssoContextData.launchData as QaLaunchData
    )
  } else if (ssoContextData.launchType === 'sr') {
    submissionStore.startSellerRatingFlow(
      hostsConfig.siteKey,
      ssoContextData.page,
      ssoContextData.launchData as SellerRatingLaunchData
    )
  } else { // only other flow
    submissionStore.startRevFlow(
      ssoContextData.sku,
      hostsConfig.siteKey,
      ssoContextData.page,
      ssoContextData.launchData
    )
  }
  submissionLoader.load()
  // stops spinner on write review button(s)
  submissionStore.isLaunchingWriteReview = false
}

export function logout() {
  if (turnToConfig.sso.logout) {
    turnToConfig.sso.logout()
  }
}

function ssoUserData(
  sku, action, authSetting, submissionToken = null, source = null, rating = null, questionId = null
) {
  const ssoContext = {} as SsoContext
  ssoContext.action = action
  ssoContext.authSetting = authSetting
  ssoContext.sku = sku
  ssoContext.submissionToken = submissionToken
  if (typeof source === 'number') {
    ssoContext.source = source
  }
  if (typeof rating === 'number') {
    ssoContext.rating = rating
  }
  if (typeof questionId === 'number') {
    ssoContext.questionId = questionId
  }
  const encodedSsoContext = encodeSsoContext(ssoContext) as string
  turnToConfig.sso.userDataFn(encodedSsoContext)
}

function parseSsoContext(ssoContext: string): SsoContext {
  let data = {} as SsoContext
  if (ssoContext) {
    // Base64 encoded JSON data (submissionToken, action)
    data = JSON.parse(atob(ssoContext)) as SsoContext
  }
  return data
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!(webpack)-strip-block!./src/services/sso-service.ts